import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatCurrency } from "@/lib/api";
import { motion } from "framer-motion";
import { Wallet, Clock, CheckCircle2, Users, Plus, ArrowUpRight, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  verifying: "bg-blue-50 text-blue-700 border-blue-200",
  approved: "bg-cyan-50 text-cyan-700 border-cyan-200",
  paid: "bg-[#00C853]/10 text-[#00a543] border-[#00C853]/30",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setOverview(r.data));
    api.get("/transactions").then((r) => setTxs(r.data));
  }, []);

  const filtered = filter ? txs.filter((t) => t.status === filter) : txs;

  const copyRef = () => {
    navigator.clipboard.writeText(`https://giftopay.com/r/${user.referral_code}`);
    toast.success("Referral link copied!");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">Dashboard</p>
            <h1 className="font-display text-3xl font-bold text-[#0B3C5D] tracking-tight mt-1">
              Welcome back, {user.name}.
            </h1>
          </div>
          <Link to="/sell"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B3C5D] text-white px-5 py-2.5 text-sm font-semibold btn-primary"
            data-testid="dash-new-sale">
            <Plus className="w-4 h-4" /> New Sale
          </Link>
        </div>

        {/* Overview cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard Icon={Wallet} label="Total Earnings" value={formatCurrency(overview?.total_earnings || 0)} accent />
          <StatCard Icon={Clock} label="Pending" value={overview?.pending_count || 0} />
          <StatCard Icon={CheckCircle2} label="Completed Payouts" value={overview?.paid_count || 0} />
          <StatCard Icon={Users} label="Referral Earnings" value={formatCurrency(overview?.referral_earnings || 0)} />
        </div>

        {/* Referral panel */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-3xl bg-navy-gradient text-white p-6 sm:p-8 grid sm:grid-cols-2 gap-6 items-center"
          data-testid="dash-referral"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/70">Refer & Earn</p>
            <h2 className="font-display text-2xl font-bold mt-1">Get 1% of every friend&apos;s sale, forever.</h2>
            <p className="mt-2 text-sm text-white/70">Your referral code:</p>
            <div className="mt-3 flex items-center gap-3">
              <code className="bg-white/10 px-3 py-2 rounded-lg font-mono text-sm">{user.referral_code}</code>
              <button onClick={copyRef} className="text-[#00C853] hover:text-white text-sm font-semibold flex items-center gap-1" data-testid="dash-copy-ref">
                <Copy className="w-4 h-4" /> Copy link
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Mini label="Clicks" value="0" />
            <Mini label="Signups" value="0" />
            <Mini label="Earned" value={formatCurrency(user.referral_earnings || 0)} />
          </div>
        </motion.div>

        {/* Transactions */}
        <div className="mt-8 bg-white rounded-3xl border border-slate-200">
          <div className="p-6 flex items-center justify-between flex-wrap gap-3 border-b border-slate-100">
            <h2 className="font-display text-lg font-bold text-[#0B3C5D]">Your transactions</h2>
            <div className="flex flex-wrap items-center gap-2">
              {["", "pending", "verifying", "approved", "paid", "rejected"].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setFilter(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    filter === s ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B3C5D]"
                  }`}
                  data-testid={`tx-filter-${s || "all"}`}
                >
                  {s || "All"}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tx-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr className="text-left">
                  <th className="px-6 py-3 font-medium">TX ID</th>
                  <th className="px-6 py-3 font-medium">Brand</th>
                  <th className="px-6 py-3 font-medium">Amount</th>
                  <th className="px-6 py-3 font-medium">Payout</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                    No transactions yet. <Link to="/sell" className="text-[#0B3C5D] underline">Start your first sale</Link>.
                  </td></tr>
                )}
                {filtered.map((t) => (
                  <tr key={t.tx_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-6 py-4 font-mono text-xs text-[#0B3C5D]">{t.tx_id}</td>
                    <td className="px-6 py-4 font-medium text-slate-900">{t.brand_name}</td>
                    <td className="px-6 py-4 text-slate-700">{formatCurrency(t.amount, t.currency)}</td>
                    <td className="px-6 py-4 font-semibold text-[#0B3C5D]">{formatCurrency(t.final_payout, t.currency)}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLES[t.status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <Link to={`/transactions/${t.tx_id}`} className="text-[#0B3C5D] hover:text-[#00C853]" data-testid={`tx-view-${t.tx_id}`}>
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ Icon, label, value, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl p-6 border ${accent ? "bg-[#0B3C5D] text-white border-transparent" : "bg-white border-slate-200"}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ? "bg-white/10" : "bg-[#00C853]/10"}`}>
        <Icon className={`w-5 h-5 ${accent ? "text-[#00C853]" : "text-[#00C853]"}`} />
      </div>
      <p className={`text-xs uppercase tracking-wider mt-4 ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</p>
      <p className={`font-display text-3xl font-bold mt-1 ${accent ? "text-white" : "text-[#0B3C5D]"}`}>{value}</p>
    </motion.div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/10 p-3">
      <p className="text-[11px] uppercase tracking-wider text-white/60">{label}</p>
      <p className="font-display font-bold text-xl mt-1">{value}</p>
    </div>
  );
}
